<script lang="ts">
    // 빠른 설정 허브 — 흩어져 있던 메인 채팅 모델·WygLore Leaf(장기기억)·MARP(멀티에이전트)
    // 설정을 한 화면에 통합. 커스터마이징을 잘 안 하는 사용자가 여기서만 만지면 바로 쓰도록.
    import { DBState, additionalSettingsMenu, SettingsMenuIndex } from "src/ts/stores.svelte";
    import { language } from "src/lang";
    import ModelList from "src/lib/UI/ModelList.svelte";
    import TextInput from "src/lib/UI/GUI/TextInput.svelte";
    import CheckInput from "src/lib/UI/GUI/CheckInput.svelte";
    import SelectInput from "src/lib/UI/GUI/SelectInput.svelte";
    import OptionInput from "src/lib/UI/GUI/OptionInput.svelte";
    import Button from "src/lib/UI/GUI/Button.svelte";
    import StatusDot from "src/lib/UI/GUI/StatusDot.svelte";
    import { getModelInfo, LLMProvider } from "src/ts/model/modellist";
    import * as marp from "src/ts/multiagent/client";
    import { alertNormal, alertError, alertConfirm } from "src/ts/alert";
    import { importCharacterProcess } from "src/ts/characterCards";
    import { getDatabase } from "src/ts/storage/database.svelte";
    import { requestImmediateSave, checkCharOrder } from "src/ts/globalApi.svelte";
    import { wygloreHealthStore, marpHealthStore, reportFeatureHealth, getFeatureHealth, type FeatureHealth } from "src/ts/featureHealth.svelte";
    import { requestChatData } from "src/ts/process/request/request";
    import type { OpenAIChat } from "src/ts/process/index.svelte";
    import { humanizeApiError } from "src/ts/errorHumanizer";
    import { wygloreServerProbe, wygloreIframeActive } from "src/ts/wyglore/nativeHost.svelte";
    import { onMount } from "svelte";

    const t = (k: string, fallback: string) => (language as any)[k] ?? fallback;

    // ── 기능 상태 표시등 (featureHealth 스토어 → StatusDot 라벨) ────────────────
    const healthLabel = (h: FeatureHealth) =>
        h.level === "ok" ? language.quickSetupStatusOk
        : h.level === "error" ? language.quickSetupStatusError
        : h.level === "off" ? language.quickSetupStatusOff
        : h.level === "unconfigured" ? t("quickSetupStatusUnconfigured", "설정 필요")
        : language.quickSetupStatusUnknown;

    let modelInfo = $derived(getModelInfo(DBState.db.aiModel));

    // ── MARP 서버 config (default_* 만 다룸 — 3 에이전트가 이걸로 폴백) ──────────
    let marpCfg = $state<any>(null);
    let marpApiKeySet = $state(false);
    let marpKeyInput = $state("");      // 비우면 기존 키 보존(서버가 empty→preserve)
    let marpLoading = $state(true);
    let marpSaving = $state(false);
    let marpStatusLine = $state("");

    // 켜져 있으나 필수 설정(모델·기본 API 키)이 비면 "설정 필요" — 장애(적색)로
    // 오인시키지 않기 위한 별도 상태. 신규 설치는 서버 기본 키가 비어 이 상태로 시작한다.
    const marpConfigured = () =>
        marpApiKeySet && !!String(marpCfg?.default_model ?? "").trim();

    function reportMarpConfigState() {
        if (DBState.db.multiagentEnabled === false) {
            reportFeatureHealth("marp", "off");
        } else if (!marpConfigured()) {
            reportFeatureHealth("marp", "unconfigured",
                t("quickSetupMarpUnconfiguredDetail", "보조 에이전트의 모델·API 키가 아직 없습니다. 아래 MARP 섹션에서 입력하고 저장하세요."));
        } else if (getFeatureHealth("marp").level !== "error") {
            // 이미 오류(에이전트 호출 실패 등)로 기록된 상태는 덮지 않는다 —
            // config 는 되는데 분석이 실패할 수 있다.
            reportFeatureHealth("marp", "ok", "config reachable");
        }
    }

    // GET /api/multiagent/config 응답은 { config, api_key_set } 래퍼다.
    // api_key_set 의 키는 SECRET_KEYS 이름 그대로(default_api_key 등)이다 —
    // 래퍼를 그대로 폼에 물리면 저장값이 항상 빈 것처럼 보인다(2026-07-08 보고).
    async function refreshMarpConfig() {
        const res = await marp.getConfig<any>();
        marpCfg = res?.config ?? null;
        marpApiKeySet = !!(res?.api_key_set?.default_api_key);
    }

    onMount(async () => {
        try {
            await refreshMarpConfig();
            reportMarpConfigState();
        } catch (e: any) {
            marpCfg = null;
            if (DBState.db.multiagentEnabled !== false) {
                reportFeatureHealth("marp", "error", `config load failed: ${e?.message ?? e}`);
            }
        } finally {
            marpLoading = false;
        }
    });

    async function saveMarpCommon() {
        if (!marpCfg) return;
        marpSaving = true;
        try {
            const body: any = { ...marpCfg };
            // 키를 새로 입력했을 때만 전송(빈 값이면 서버가 기존 키 보존)
            if (marpKeyInput.trim()) body.default_api_key = marpKeyInput.trim();
            else body.default_api_key = "";
            await marp.putConfig<any>(body);
            // PUT 응답은 설정이 아니라 상태 객체(statusResponse)다 — 폼에 쓰지 말고
            // redacted config 를 다시 받아 폼·상태등을 동기화한다.
            await refreshMarpConfig();
            marpKeyInput = "";
            // 저장으로 미설정이 해소됐으면 상태등을 "설정 필요"에서 풀어준다.
            reportMarpConfigState();
            alertNormal(t("quickSetupSaved", "저장되었습니다."));
        } catch (e: any) {
            alertError(String(e?.message ?? e));
        } finally {
            marpSaving = false;
        }
    }

    async function testMarp() {
        marpStatusLine = t("quickSetupTesting", "테스트 중...");
        try {
            const r = await marp.test<any>("");
            const results = r?.results ?? [];
            const ok = results.filter((x: any) => x.success).length;
            marpStatusLine = `${ok}/${results.length} ${t("quickSetupAgentsOk", "에이전트 정상")}`;
            reportFeatureHealth("marp", ok > 0 ? "ok" : "error", `test ${ok}/${results.length}`);
        } catch (e: any) {
            marpStatusLine = t("quickSetupTestFail", "테스트 실패: ") + String(e?.message ?? e);
            reportFeatureHealth("marp", "error", `test failed: ${e?.message ?? e}`);
        }
    }

    // ── 연결 테스트 (MARP 의 "연결 테스트"를 전 기능으로 일반화) ────────────────
    // 각 테스트는 성공 여부를 반환해 "전체 진단"이 합산한다. 결과는 인라인
    // 한 줄(초록/빨강)로 보여주고, 실패 원문은 errorHumanizer 로 번역한다.

    let mainTestBusy = $state(false);
    let mainTestOk = $state<boolean | null>(null);
    let mainTestLine = $state("");

    async function testMainModel(): Promise<boolean> {
        if (mainTestBusy) return false;
        mainTestBusy = true;
        mainTestOk = null;
        mainTestLine = t("quickSetupTesting", "테스트 중...");
        try {
            // 실제 파이프라인으로 초소형 요청 1회 — 키·모델·네트워크를 한 번에 검증.
            // blockPlugins=true 는 before/afterRequest replacer 전체를 건너뛰어
            // WygLore 회상·MARP 분석이 이 순수 연결 테스트에 발동하지 않게 한다
            // (request.ts 의 replacer 루프가 blockPlugins 를 존중).
            const res = await requestChatData({
                formated: [{ role: "user", content: "Connection test. Reply with: OK" } as OpenAIChat],
                bias: {},
                useStreaming: false,
                maxTokens: 16,
                noMultiGen: true,
                blockPlugins: true,
            }, "model");
            if (res.type !== "fail") {
                mainTestOk = true;
                mainTestLine = t("quickSetupModelTestOk", "정상 — 모델이 응답했습니다.");
                return true;
            }
            const raw = typeof res.result === "string" ? res.result : JSON.stringify(res.result);
            const h = humanizeApiError(raw);
            mainTestOk = false;
            mainTestLine = h ? `${h.title} ${h.hint}` : t("quickSetupTestFail", "테스트 실패: ") + raw.slice(0, 200);
            return false;
        } catch (e: any) {
            const raw = String(e?.message ?? e);
            const h = humanizeApiError(raw);
            mainTestOk = false;
            mainTestLine = h ? `${h.title} ${h.hint}` : t("quickSetupTestFail", "테스트 실패: ") + raw.slice(0, 200);
            return false;
        } finally {
            mainTestBusy = false;
        }
    }

    let wygloreTestBusy = $state(false);
    let wygloreTestOk = $state<boolean | null>(null);
    let wygloreTestLine = $state("");

    async function testWyglore(): Promise<boolean> {
        if (wygloreTestBusy) return false;
        wygloreTestBusy = true;
        wygloreTestOk = null;
        wygloreTestLine = t("quickSetupTesting", "테스트 중...");
        try {
            // 클라이언트 절반(내장 iframe 기동) + 서버 절반(그래프 스토어 비파괴 프로브)
            const engineUp = wygloreIframeActive();
            const probe = await wygloreServerProbe();
            if (engineUp && probe.ok) {
                wygloreTestOk = true;
                wygloreTestLine = t("quickSetupWygloreTestOk", "정상 — 내장 엔진과 기억 서버 모두 응답했습니다.");
                return true;
            }
            wygloreTestOk = false;
            wygloreTestLine = !engineUp
                ? t("quickSetupWygloreTestNoEngine", "내장 엔진이 기동되지 않았습니다. 새로고침 후 다시 시도하세요.")
                : t("quickSetupWygloreTestNoServer", "기억 서버 응답 실패: ") + probe.detail;
            return false;
        } finally {
            wygloreTestBusy = false;
        }
    }

    // 전체 진단: 위 테스트 3종을 순차 실행하고 항목별 색점(상태등과 동일 문법)으로 요약.
    // 색점만으로는 색각 이상 사용자가 성패를 구분할 수 없어 상태 텍스트를 항상 부기한다.
    type DiagStatus = "ok" | "fail" | "off" | "setup";
    let diagBusy = $state(false);
    let diagResults = $state<{ label: string; status: DiagStatus }[]>([]);

    const diagLevel = (s: DiagStatus): FeatureHealth["level"] =>
        s === "ok" ? "ok" : s === "fail" ? "error" : s === "setup" ? "unconfigured" : "off";
    const diagText = (s: DiagStatus) =>
        s === "ok" ? t("quickSetupDiagOkShort", "정상")
        : s === "fail" ? t("quickSetupDiagFailShort", "실패")
        : s === "setup" ? t("quickSetupStatusUnconfigured", "설정 필요")
        : language.quickSetupStatusOff;

    async function runFullDiagnostics() {
        if (diagBusy) return;
        diagBusy = true;
        diagResults = [];
        try {
            const results: { label: string; status: DiagStatus }[] = [];
            const okModel = await testMainModel();
            results.push({ label: t("quickSetupMainModel", "메인 채팅 모델"), status: okModel ? "ok" : "fail" });

            if (DBState.db.wygloreEnabled === false) {
                results.push({ label: "WygLore", status: "off" });
            } else {
                const okWyg = await testWyglore();
                results.push({ label: "WygLore", status: okWyg ? "ok" : "fail" });
            }

            if (DBState.db.multiagentEnabled === false) {
                results.push({ label: "MARP", status: "off" });
            } else {
                await testMarp();
                const marpLevel = getFeatureHealth("marp").level;
                results.push({
                    label: "MARP",
                    status: marpLevel === "ok" ? "ok" : marpLevel === "unconfigured" ? "setup" : "fail",
                });
            }
            diagResults = results;
        } finally {
            diagBusy = false;
        }
    }

    // ── 예시(스타터) 캐릭터 ────────────────────────────────────────────────
    const STARTER = { url: "/starter/anomaly-protocol.json", name: "Anomaly Protocol" };
    let starterBusy = $state(false);

    async function addStarterCharacter() {
        if (starterBusy) return;
        const db = getDatabase();
        const exists = (db.characters ?? []).some(
            (c: any) => c?.type !== "group" && c?.name === STARTER.name
        );
        if (exists) {
            const go = await alertConfirm(
                t("quickSetupStarterDup", `이미 '${STARTER.name}' 캐릭터가 있습니다. 그래도 추가할까요?`)
            );
            if (!go) return;
        }
        starterBusy = true;
        try {
            const res = await fetch(STARTER.url, { cache: "no-store" });
            if (!res.ok) throw new Error(`fetch ${res.status}`);
            const buf = new Uint8Array(await res.arrayBuffer());
            const before = getDatabase()?.characters?.length ?? 0;
            await importCharacterProcess({ name: "anomaly-protocol.json", data: buf });
            checkCharOrder();
            const after = getDatabase()?.characters?.length ?? 0;
            if (after > before) {
                try { await requestImmediateSave({ forceFullWrite: true }); } catch (_) {}
                alertNormal(t("quickSetupStarterAdded", `'${STARTER.name}' 캐릭터를 추가했습니다. 목록에서 확인하세요.`));
            } else {
                alertError(t("quickSetupStarterFail", "캐릭터 추가에 실패했습니다."));
            }
        } catch (e: any) {
            alertError(String(e?.message ?? e));
        } finally {
            starterBusy = false;
        }
    }

    function openWyglorePanel() {
        const entry = additionalSettingsMenu.find((m) => /wyglore/i.test(m.name ?? ""));
        if (entry && typeof entry.callback === "function") {
            entry.callback();
        } else {
            alertNormal(t("quickSetupWygloreNotReady", "WygLore 패널이 아직 준비되지 않았습니다. 새로고침 후 다시 시도하세요."));
        }
    }
</script>

<div class="flex flex-col p-3 gap-2">
    <h2 class="text-2xl font-bold text-textcolor">{t("quickSetupTitle", "빠른 설정")}</h2>
    <span class="text-textcolor2 text-sm mb-2">
        {t("quickSetupDesc", "채팅에 필요한 핵심 설정을 한곳에 모았습니다. 아래만 채우면 바로 대화할 수 있어요.")}
    </span>

    <!-- ── 전체 연결 진단 (모델·WygLore·MARP 순차 테스트) ─────────────────── -->
    <div class="flex items-center gap-2 flex-wrap mb-2">
        <Button size="sm" styled="outlined" disabled={diagBusy} onclick={runFullDiagnostics}>
            {diagBusy ? t("quickSetupDiagRunning", "진단 실행 중...") : t("quickSetupDiagRun", "전체 연결 진단")}
        </Button>
        {#each diagResults as r}
            <StatusDot level={diagLevel(r.status)} label={`${r.label} (${diagText(r.status)})`} />
        {/each}
    </div>

    <!-- ── 1. 메인 채팅 모델 ──────────────────────────────────────────── -->
    <div class="rounded-md border border-darkborderc p-3 flex flex-col gap-1 mb-2">
        <span class="text-lg font-semibold text-textcolor">① {t("quickSetupMainModel", "메인 채팅 모델")}</span>
        <span class="text-textcolor2 text-xs mb-1">{t("quickSetupMainModelDesc", "실제 답변을 생성하는 모델과 API 키입니다.")}</span>

        <span class="text-textcolor mt-2">{language.model}</span>
        <ModelList bind:value={DBState.db.aiModel} />

        {#if modelInfo.provider === LLMProvider.GoogleCloud}
            <span class="text-textcolor mt-3">GoogleAI API Key</span>
            <TextInput className="mt-1" placeholder="AIza..." hideText={DBState.db.hideApiKey} bind:value={DBState.db.google.accessToken} />
        {:else if modelInfo.provider === LLMProvider.OpenAI}
            <span class="text-textcolor mt-3">OpenAI API Key</span>
            <TextInput className="mt-1" placeholder="sk-..." hideText={DBState.db.hideApiKey} bind:value={DBState.db.openAIKey} />
        {:else if modelInfo.provider === LLMProvider.Anthropic}
            <span class="text-textcolor mt-3">Claude API Key</span>
            <TextInput className="mt-1" placeholder="sk-ant-..." hideText={DBState.db.hideApiKey} bind:value={DBState.db.claudeAPIKey} />
        {:else if modelInfo.provider === LLMProvider.Mistral}
            <span class="text-textcolor mt-3">Mistral API Key</span>
            <TextInput className="mt-1" placeholder="..." hideText={DBState.db.hideApiKey} bind:value={DBState.db.mistralKey} />
        {:else}
            <span class="text-textcolor2 text-xs mt-3">
                {t("quickSetupKeyElsewhere", "이 프로바이더의 키는 아래 '고급 모델 설정'에서 입력하세요.")}
            </span>
        {/if}

        <div class="mt-3 flex gap-2 items-center flex-wrap">
            <Button size="sm" styled="outlined" disabled={mainTestBusy} onclick={testMainModel}>
                {mainTestBusy ? t("quickSetupTesting", "테스트 중...") : t("quickSetupTest", "연결 테스트")}
            </Button>
            <!-- index 1 = AI 모델(BotSettings). 2(보조 기능)로 잘못 보내던 버그 수정. -->
            <Button size="sm" styled="outlined" onclick={() => { $SettingsMenuIndex = 1 }}>
                {t("quickSetupAdvancedModel", "고급 모델 설정 열기")}
            </Button>
        </div>
        {#if mainTestLine}
            <span class="text-xs mt-1 {mainTestOk === true ? 'text-status-ok' : mainTestOk === false ? 'text-status-bad' : 'text-textcolor2'}">{mainTestLine}</span>
        {/if}
        <span class="text-textcolor2 text-xs mt-1">{t("quickSetupModelTestHint", "연결 테스트는 설정된 모델로 실제 1회 초소형 호출을 보냅니다(소량의 토큰 소비).")}</span>
    </div>

    <!-- ── 2. 장기기억 (WygLore Leaf) ─────────────────────────────────── -->
    <div class="rounded-md border border-darkborderc p-3 flex flex-col gap-1 mb-2">
        <span class="text-lg font-semibold text-textcolor flex items-center gap-2 flex-wrap">
            ② {t("quickSetupWyglore", "장기기억 (WygLore Leaf)")}
            <StatusDot level={$wygloreHealthStore.level} label={healthLabel($wygloreHealthStore)} detail={$wygloreHealthStore.detail ?? ""} />
        </span>
        <span class="text-textcolor2 text-xs mb-1">
            {t("quickSetupWygloreDesc", "대화 속 세계관·인물·사건을 연상 기억으로 축적해 일관성을 높입니다. 기본 켜짐.")}
        </span>
        <CheckInput
            bind:check={DBState.db.wygloreEnabled}
            name={t("quickSetupEnable", "사용")}
        />
        <span class="text-textcolor2 text-xs">{t("quickSetupRestartHint", "켜고 끔은 새로고침 후 적용됩니다.")}</span>
        <div class="mt-2 flex gap-2 items-center flex-wrap">
            <!-- 위그로어 B안 관례: 사용 불가 시 버튼을 숨기지 않고 비활성+사유로 노출.
                 사유는 title(hover)이 아니라 인라인 텍스트 — 터치 기기에서도 보이도록. -->
            <Button size="sm" styled="outlined"
                disabled={wygloreTestBusy || DBState.db.wygloreEnabled === false}
                onclick={testWyglore}>
                {wygloreTestBusy ? t("quickSetupTesting", "테스트 중...") : t("quickSetupTest", "연결 테스트")}
            </Button>
            <Button size="sm" styled="outlined" disabled={DBState.db.wygloreEnabled === false} onclick={openWyglorePanel}>
                {t("quickSetupWygloreOpen", "WygLore 상세 설정 열기")}
            </Button>
        </div>
        {#if DBState.db.wygloreEnabled === false}
            <span class="text-textcolor2 text-xs mt-1">{t("quickSetupWygloreTestOffReason", "기능이 꺼져 있습니다. 켜고 새로고침하면 테스트할 수 있어요.")}</span>
        {/if}
        {#if wygloreTestLine}
            <span class="text-xs mt-1 {wygloreTestOk === true ? 'text-status-ok' : wygloreTestOk === false ? 'text-status-bad' : 'text-textcolor2'}">{wygloreTestLine}</span>
        {/if}
        <span class="text-textcolor2 text-xs mt-1">{t("quickSetupWygloreTestHint", "내장 엔진 기동 여부와 기억 서버 도달성만 확인합니다(데이터 변경·토큰 소비 없음). 임베딩 프로바이더 테스트는 WygLore 상세 설정 안에 있습니다.")}</span>
    </div>

    <!-- ── 3. 멀티에이전트 RP (MARP) ──────────────────────────────────── -->
    <div class="rounded-md border border-darkborderc p-3 flex flex-col gap-1">
        <span class="text-lg font-semibold text-textcolor flex items-center gap-2 flex-wrap">
            ③ {t("quickSetupMarp", "멀티에이전트 RP (MARP)")}
            <StatusDot level={$marpHealthStore.level} label={healthLabel($marpHealthStore)} detail={$marpHealthStore.detail ?? ""} />
        </span>
        <span class="text-textcolor2 text-xs mb-1">
            {t("quickSetupMarpDesc", "메인 모델이 답하기 전에 세계관·플롯·인물 보조 에이전트가 일관성 노트를 뽑아 주입합니다. 턴당 추가 호출이 발생합니다.")}
        </span>
        <CheckInput
            bind:check={DBState.db.multiagentEnabled}
            name={t("quickSetupEnable", "사용")}
        />

        {#if DBState.db.multiagentEnabled}
            <div class="flex flex-col gap-1 mt-2 pl-1 border-l-2 border-darkborderc">
                <span class="text-textcolor mt-1 text-sm">{t("quickSetupMarpProvider", "보조 에이전트 공용 프로바이더")}</span>
                {#if marpLoading}
                    <span class="text-textcolor2 text-xs">{t("quickSetupLoading", "불러오는 중...")}</span>
                {:else if !marpCfg}
                    <span class="text-textcolor2 text-xs">{t("quickSetupMarpUnavailable", "MARP 서버 설정을 불러올 수 없습니다.")}</span>
                {:else}
                    <SelectInput bind:value={marpCfg.default_provider}>
                        <OptionInput value="openai-compatible">{t("quickSetupMarpProviderOpenAICompat", "OpenAI 호환")}</OptionInput>
                        <OptionInput value="anthropic">Anthropic</OptionInput>
                        <OptionInput value="vertex-ai">Vertex AI</OptionInput>
                    </SelectInput>
                    <span class="text-textcolor text-sm mt-2">Base URL</span>
                    <TextInput className="mt-1" placeholder="https://api.openai.com/v1" bind:value={marpCfg.default_base_url} />
                    <span class="text-textcolor text-sm mt-2">{t("quickSetupMarpModel", "모델")}</span>
                    <TextInput className="mt-1" placeholder="gpt-4o-mini" bind:value={marpCfg.default_model} />
                    <span class="text-textcolor text-sm mt-2">API Key {marpApiKeySet ? t("quickSetupKeySet", "(설정됨 — 비우면 유지)") : ""}</span>
                    <TextInput className="mt-1" placeholder={marpApiKeySet ? "••••••••" : "sk-..."} hideText={true} bind:value={marpKeyInput} />

                    <div class="flex gap-2 mt-3 items-center flex-wrap">
                        <Button size="sm" styled="primary" disabled={marpSaving} onclick={saveMarpCommon}>
                            {marpSaving ? t("quickSetupSaving", "저장 중...") : t("quickSetupSave", "저장")}
                        </Button>
                        <Button size="sm" styled="outlined" onclick={testMarp}>
                            {t("quickSetupTest", "연결 테스트")}
                        </Button>
                        {#if marpStatusLine}
                            <span class="text-textcolor2 text-xs">{marpStatusLine}</span>
                        {/if}
                    </div>

                    <span class="text-textcolor text-sm mt-3">{t("quickSetupMarpInjection", "주입 형식 / 위치")}</span>
                    <div class="flex gap-2">
                        <SelectInput bind:value={DBState.db.multiagentInjectionFormat}>
                            <OptionInput value="classic">classic</OptionInput>
                            <OptionInput value="xml">xml</OptionInput>
                            <OptionInput value="markdown-table">markdown-table</OptionInput>
                        </SelectInput>
                        <SelectInput bind:value={DBState.db.multiagentInjectionPosition}>
                            <OptionInput value="system-end">system-end</OptionInput>
                            <OptionInput value="before-last-user">before-last-user</OptionInput>
                        </SelectInput>
                    </div>
                    <span class="text-textcolor2 text-xs">{t("quickSetupMarpInjectionHint", "잘 모르면 기본값(classic / system-end)을 그대로 두세요.")}</span>
                {/if}
            </div>
        {/if}
    </div>

    <!-- ── 4. 예시(스타터) 캐릭터 ─────────────────────────────────────── -->
    <div class="rounded-md border border-darkborderc p-3 flex flex-col gap-1 mt-2">
        <span class="text-lg font-semibold text-textcolor">④ {t("quickSetupStarter", "예시 캐릭터")}</span>
        <span class="text-textcolor2 text-xs mb-1">
            {t("quickSetupStarterDesc", "바로 대화를 시작할 수 있는 예시 캐릭터 'Anomaly Protocol'(기밀 조사형 어반 판타지)을 추가합니다.")}
        </span>
        <div class="mt-1">
            <Button size="sm" styled="primary" disabled={starterBusy} onclick={addStarterCharacter}>
                {starterBusy ? t("quickSetupStarterAdding", "추가 중...") : t("quickSetupStarterAdd", "Anomaly Protocol 추가")}
            </Button>
        </div>
    </div>
</div>
